import { CapabilityButton } from "../CapabilityButton";
import { permissionNames as P } from "../../auth/permissions.generated";
import { useCapability } from '../../hooks/useCapability';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Settings2, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { useApiService } from '../../services/apiService';
import { ProductComparerApiService } from '../../services/productComparerService';
import { ProductComparerSearch } from './ProductComparerSearch';
import { ProductComparerTable } from './ProductComparerTable';
import { ProductComparerSettings, loadComparisonSettings } from './ProductComparerSettings';
import type { NavigationItem } from '../Navigation';
import type {
    ProductSummary,
    ArticleGraph,
    ComparableFieldDefinition,
    ComparisonSettings,
    ProductComparerAiAnalysisResponse,
    ProductComparerView,
} from '../../types/productComparer';

interface ProductComparerProps {
    onNavigate: (view: NavigationItem) => void;
    itemsToCompare?: ProductSummary[];
    onReturnToSearch?: () => void;
}

export default function ProductComparer({ onNavigate, itemsToCompare, onReturnToSearch }: ProductComparerProps) {
    const allows = useCapability();
    const api = useApiService();
    const comparerApi = useMemo(() => new ProductComparerApiService(api), [api]);

    const [view, setView] = useState<ProductComparerView>('search');
    const [comparisonData, setComparisonData] = useState<ArticleGraph[]>([]);
    const [availableFields, setAvailableFields] = useState<ComparableFieldDefinition[]>([]);
    const [settings, setSettings] = useState<ComparisonSettings>(loadComparisonSettings);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [aiAnalysis, setAiAnalysis] = useState<ProductComparerAiAnalysisResponse | null>(null);
    const startedComparison = useRef(false);

    useEffect(() => {
        comparerApi.getAvailableFields().then(setAvailableFields).catch(console.error);
    }, [comparerApi]);

    const handleSearch = useCallback(
        async (query: string): Promise<ProductSummary[]> => {
            return comparerApi.searchItems(query);
        },
        [comparerApi]
    );

    const handleCompare = useCallback(
        async (items: ProductSummary[]) => {
            if (!allows(P.ProductComparisonCompare)) return;
            setLoading(true);
            setError(null);
            setAiAnalysis(null);
            try {
                const entityIds = items.map(i => i.entityId);
                const articles = await comparerApi.compareItems(entityIds);
                const analysis = allows(P.ProductComparisonAnalyze) ? await comparerApi.analyzeWithAi({
                    entityIds,
                    selectedFieldIds: settings.selectedFieldIds,
                    innovationStrategy: settings.innovationStrategy,
                    salesStrategy: settings.salesStrategy,
                }) : null;

                setComparisonData(articles);
                setAiAnalysis(analysis);
                setView('compare');
            } catch {
                setError('Failed to load comparison data. Please try again.');
            } finally {
                setLoading(false);
            }
        },
        [comparerApi, settings, allows]
    );

    const handleSettingsChanged = useCallback((newSettings: ComparisonSettings) => {
        setSettings(newSettings);
    }, []);

    useEffect(() => {
        if (itemsToCompare && !startedComparison.current) {
            startedComparison.current = true;
            void handleCompare(itemsToCompare);
        }
    }, [itemsToCompare, handleCompare]);

    const handleExportExcel = useCallback(async () => {
        try {
            setExportLoading(true);
            setError(null);

            const entityIds = comparisonData.map(item => item.entityId);
            if (entityIds.length < 2) {
                setError('At least two products are required for export.');
                return;
            }

            const blob = await comparerApi.exportToExcel({
                entityIds,
                selectedFieldIds: settings.selectedFieldIds,
                innovationStrategy: settings.innovationStrategy,
                salesStrategy: settings.salesStrategy,
            });

            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `product-comparison-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xlsx`;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(url);
        } catch {
            setError('Failed to export Excel report. Please try again.');
        } finally {
            setExportLoading(false);
        }
    }, [comparerApi, comparisonData, settings]);

    return (
        <div className="space-y-4">
            {/* Top bar */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onReturnToSearch ? onReturnToSearch() : onNavigate('product-dashboard')}
                    >
                        <ArrowLeft className="mr-1 h-4 w-4" /> {onReturnToSearch ? 'Back to Search' : 'Products'}
                    </Button>
                    {view === 'compare' && !onReturnToSearch && (
                        <Button variant="ghost" size="sm" onClick={() => setView('search')}>
                            ← Back to Search
                        </Button>
                    )}
                    {view === 'settings' && (
                        <Button variant="ghost" size="sm" onClick={() => setView(itemsToCompare && comparisonData.length ? 'compare' : 'search')}>
                            {itemsToCompare ? '← Back to Comparison' : '← Back to Search'}
                        </Button>
                    )}
                </div>
                {view !== 'settings' && (
                    <Button variant="outline" size="sm" onClick={() => setView('settings')}>
                        <Settings2 className="mr-1 h-4 w-4" /> Settings
                    </Button>
                )}
            </div>

            {/* Loading Overlay */}
            {loading && (
                <div className="flex items-center justify-center py-20">
                    <div className="flex flex-col items-center gap-3 text-muted-foreground">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
                        <p className="text-sm">Loading comparison data…</p>
                    </div>
                </div>
            )}

            {error && (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                    {error}
                    {itemsToCompare && <CapabilityButton capability={P.ProductComparisonCompare} className="ml-3" variant="outline" disabled={loading} onClick={() => void handleCompare(itemsToCompare)}>Try again</CapabilityButton>}
                </div>
            )}

            {/* Views */}
            {!loading && view === 'search' && !itemsToCompare && (
                <ProductComparerSearch
                    onCompare={handleCompare}
                    onSearch={handleSearch}
                    onOpenSettings={() => setView('settings')}
                />
            )}

            {!loading && view === 'compare' && (
                <ProductComparerTable
                    items={comparisonData}
                    settings={settings}
                    aiAnalysis={aiAnalysis}
                    availableFields={availableFields}
                    onExportExcel={handleExportExcel}
                    exportLoading={exportLoading}
                />
            )}

            {!loading && view === 'settings' && (
                <ProductComparerSettings
                    fields={availableFields}
                    onClose={() => setView(itemsToCompare ? 'compare' : 'search')}
                    onSettingsChanged={handleSettingsChanged}
                />
            )}
        </div>
    );
}
